import { useCallback, useEffect, useState } from "react";
import { Search, Loader2, Hash, UserRound } from "lucide-react";
import { toast } from "sonner";
import { getJobs, getTeam, assignJob } from "@/lib/api";

function JobCard({ job, team, onAssigned }) {
  const [saving, setSaving] = useState(false);
  const change = async (e) => {
    const userId = e.target.value;
    setSaving(true);
    try {
      const r = await assignJob(job.id, userId);
      toast.success(`${job.ref || job.customer_name} reassigned to ${r.assigned_to}`);
      onAssigned();
    } catch (err) {
      toast.error("Couldn't reassign", { description: err?.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-[var(--border)] bg-black/20 p-3 space-y-2" data-testid={`job-card-${job.id}`}>
      <div className="flex items-start gap-2">
        <div data-testid={`job-ref-${job.id}`}
          className={`shrink-0 flex items-center gap-1 rounded-md border px-2 py-1 font-mono-tech text-xs font-bold ${job.ref ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
          <Hash size={11} />{job.ref || "No ref"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{job.customer_name || "—"}</p>
          <p className="text-[11px] font-mono-tech text-[var(--text-muted)]">{job.date} {job.start_time}{job.end_time ? `–${job.end_time}` : ""} · {job.status}</p>
        </div>
      </div>
      {job.description && <p className="text-xs text-[var(--text-secondary)] line-clamp-2">{job.description}</p>}
      <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
        <UserRound size={13} className="text-[var(--tech-accent)] shrink-0" />
        <select data-testid={`job-assign-${job.id}`} value={job.assigned_to_user_id || ""} onChange={change} disabled={saving}
          className="flex-1 min-w-0 rounded-lg bg-black/40 border border-[var(--border)] px-2 py-1.5 text-xs text-[var(--text-primary)] outline-none focus:border-[var(--tech-accent)]">
          {!job.assigned_to_user_id && <option value="">Unassigned</option>}
          {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {saving && <Loader2 size={13} className="animate-spin" />}
      </label>
    </div>
  );
}

const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Adelaide" });
const FILTERS = [
  { id: "all", label: "All", test: () => true },
  { id: "today", label: "Today", test: (j) => j.date === todayStr() },
  { id: "unassigned", label: "Unassigned", test: (j) => !j.assigned_to_user_id },
  { id: "tickets", label: "Tickets", test: (j) => (j.ref || "").startsWith("TKT") },
];

export function JobsPanel({ search }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try { setJobs(await getJobs(query)); } catch { toast.error("Couldn't load jobs from Timekeeper"); }
    setLoading(false);
  }, []);
  useEffect(() => {
    getTeam().then(setTeam).catch(() => {});
    const initial = search?.q || "";
    setQ(initial); setFilter("all"); load(initial);
  }, [load, search]);

  const shown = jobs.filter(FILTERS.find((f) => f.id === filter).test);
  const submit = (e) => { e.preventDefault(); load(q.trim()); };

  return (
    <div className="p-4 space-y-3" data-testid="jobs-panel">
      <form onSubmit={submit} className="flex gap-2">
        <div className="flex-1 flex items-center gap-2 rounded-xl bg-black/30 border border-[var(--border)] px-3 focus-within:border-[var(--tech-accent)] transition-colors">
          <Search size={14} className="text-[var(--text-muted)] shrink-0" />
          <input data-testid="jobs-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="TKT-1234, BKG-…, WRN-… or customer" autoCapitalize="characters"
            className="w-full bg-transparent py-2.5 text-sm outline-none" />
        </div>
        <button data-testid="jobs-search-button" className="rounded-xl bg-[var(--tech-accent)] text-black text-xs font-semibold px-3 hover:opacity-90 transition-opacity">Search</button>
      </form>
      <div className="flex gap-1.5 flex-wrap" data-testid="jobs-filters">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`jobs-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-200 ${filter === f.id ? "border-[var(--tech-accent)] bg-[var(--tech-accent)] text-black" : "border-[var(--border)] text-[var(--text-secondary)] hover:text-white"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[var(--text-muted)]" data-testid="jobs-count">
        {loading ? "Loading from Timekeeper…" : `${shown.length} job${shown.length === 1 ? "" : "s"}${q.trim() ? " found (incl. closed)" : " open"}`}
      </p>
      {shown.map((j) => <JobCard key={j.id} job={j} team={team} onAssigned={() => load(q.trim())} />)}
    </div>
  );
}
